import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import styles from './dropdown.module.scss';

export interface DropdownItem<T = string> {
  /** Whether the item can be selected */
  isDisabled?: boolean;
  label: string;
  subText?: string;
  /** Bold text shown after the label on the same line, e.g. availability */
  suffix?: string;
  value: T;
}

export interface DropdownProps<T = string> {
  /** Additional class names */
  className?: string;
  /** The dropdown icon to show */
  icon?: IconName;
  /** Whether the dropdown is disabled */
  isDisabled?: boolean;
  /** The dropdown items */
  items?: DropdownItem<T>[];
  /** The dropdown label */
  label: string;
  /** The change handler */
  onChange: (value: T) => void;
  /** The text shown when nothing is selected, defaults to the label */
  placeholder?: string;
  /** The value of the active item */
  value?: T;
}

const Dropdown = <ValueType,>({
  className,
  icon,
  isDisabled,
  items = [],
  label,
  onChange,
  placeholder,
  value,
}: DropdownProps<ValueType>) => {
  const [isOpen, setIsOpen] = useState(false);

  const activeItem = useMemo(() => {
    return items.find(item => item.value === value);
  }, [items, value]);

  const handleChange = (value: ValueType) => {
    onChange(value);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    if (!window) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!target.closest(`.${styles.component}`)) {
        setIsOpen(false);
      }
    };

    window.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isDisabled]: isDisabled || items.length === 0,
        [styles.isOpen]: isOpen,
      })}
    >
      <button
        className={classNames(styles.item, styles.trigger)}
        onClick={() => setIsOpen(previousState => !previousState)}
        tabIndex={0}
      >
        <div className={styles.textsWithIcon}>
          {icon && <Icon name={icon} size={24} />}

          <div className={styles.texts}>
            <Typography className={styles.label} tagStyle="bodySmallBold">
              {label}
            </Typography>

            <Typography className={styles.value} tagStyle="bodyMedium">
              {activeItem?.label || placeholder || label}
            </Typography>
          </div>
        </div>

        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={12} />
      </button>

      {items.length && (
        <div className={styles.items}>
          {items.map((item, itemIndex) => (
            <button
              key={itemIndex}
              className={classNames(styles.item, {
                [styles.isItemDisabled]: item.isDisabled,
                [styles.isSelected]: item.value === activeItem?.value,
              })}
              disabled={item.isDisabled}
              onClick={() => handleChange(item.value)}
              tabIndex={isOpen ? 0 : undefined}
            >
              <Typography tagStyle="bodyMedium">
                {item.label}
                {item.suffix && (
                  <React.Fragment>
                    {' - '}
                    <strong>{item.suffix}</strong>
                  </React.Fragment>
                )}
              </Typography>

              {item.subText && (
                <Typography tagStyle="bodySmall">{item.subText}</Typography>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

Dropdown.displayName = 'Dropdown';

export default Dropdown;
